import Image from 'next/image';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useUser from '@alltrails/context/hooks/useUser';
import Typography from '@alltrails/core/components/Typography';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import Button from '@alltrails/denali/components/Button';
import PeakLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Peak';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import useDeeplink from '@alltrails/install-prompt/hooks/useDeeplink';
import { ModalAriaLabelledByHeading, ModalStep } from '@alltrails/modal';
import type { User } from '@alltrails/types/User';
import styles from './CheckoutWelcome.module.scss';
import qrCodeSrc from './qr-code.svg';

type Props = {
  closeModal: (user: User) => void;
  purchaseTier: PurchaseTier;
};

// https://www.figma.com/design/dOj6Te4I0TVguYevn6Fj5g/Web-Checkout-Improvements?node-id=334-31701&m=dev
// https://www.figma.com/design/dOj6Te4I0TVguYevn6Fj5g/Web-Checkout-Improvements?node-id=375-33022&m=dev
// https://www.figma.com/design/CAAZrtC2gy6z6B5zwkuTm4/Peak%2BPlus-Badging-and-Content?node-id=495-137216&m=dev
export default function CheckoutWelcome({ closeModal, purchaseTier }: Props) {
  const intl = useIntl();
  const user = useUser();
  const getDeeplink = useDeeplink({});

  const handleCloseRequest = useCallback(() => {
    closeModal(user!); // user should always be defined at checkout welcome step.
  }, [closeModal, user]);

  return (
    <ModalStep contentsClassName={styles.contents} hideCloseButton onCloseRequest={handleCloseRequest}>
      <div className={styles.content}>
        {purchaseTier === 'peak' ? <PeakLogoBadge size="xl" variant="icon" /> : <PlusLogoBadge size="xl" variant="icon" />}
        <div className={styles.heading}>
          <ModalAriaLabelledByHeading as="h2" size="400">
            {purchaseTier === 'peak' ? <FormattedMessage defaultMessage="Welcome to Peak" /> : <FormattedMessage defaultMessage="Welcome to Plus" />}
          </ModalAriaLabelledByHeading>
          <Typography color="secondary" variant="text200">
            {purchaseTier === 'peak' ? (
              <FormattedMessage defaultMessage="Head to the app to identify plants, build custom routes on the go, and more" />
            ) : (
              <FormattedMessage defaultMessage="Head to the app to explore offline, get alerts for wrong turns, and more" />
            )}
          </Typography>
        </div>
        <Image
          alt={intl.formatMessage({
            defaultMessage:
              'This QR code is intended to scan from a smartphone. If you have the AllTrails app, the link will open the app for you. If you don’t have the app, the link will take you to your device’s app store.'
          })}
          className={styles.qr}
          src={qrCodeSrc}
        />
        <div className={styles.buttons}>
          <Button
            linkInfo={{ href: getDeeplink() }}
            text={<FormattedMessage defaultMessage="Go to the app" />}
            variant="primary"
          />
          <Button onClick={handleCloseRequest} text={<FormattedMessage defaultMessage="Got it" />} />
        </div>
      </div>
    </ModalStep>
  );
}
